import { observer } from 'mobx-react-lite'
import React, { useEffect, useState } from 'react'
import { IconName } from '@fortawesome/fontawesome-svg-core'
import { useNavigate } from 'react-router'

//evertel
import { useService } from '@evertel/di'
import { SessionService } from '@evertel/session/SessionService'
import { Card, Icon } from '@evertel/web/ui'
import { PushService } from '@evertel/push'
import { CurrentUserController, UnreadCountsState, UserSettingsController } from '@evertel/blue-user'
import { useJoinRoom } from '@evertel/web/room'
import { useCreateThread } from '@evertel/web/thread'
import { useInvite } from '@evertel/web/invites'
import { JoinDepartmentModal } from '@evertel/web/department'
import { VerifiedEmailDomainController } from '@evertel/shared/verified-email-domain'


const OnboardingStepsAction: React.FC = observer(() => {

    const pushService = useService(PushService, [])
    const sessionService = useService(SessionService, [])
    const unreadCountsState = useService(UnreadCountsState, [])
    const userSettingsController = useService(UserSettingsController, [])
    const verifiedEmailDomainController = useService(VerifiedEmailDomainController, [])
    const currentUserController = useService(CurrentUserController, [])

    const [isJoinDepartmentModalOpen, setIsJoinDepartmentModalOpen] = useState(false)
    const [isGovernmentEmail, setIsGovernmentEmail] = useState<boolean>(false)

    const navigate = useNavigate()
    const { openJoinRoom } = useJoinRoom()
    const { openCreateThread } = useCreateThread()
    const { openInvite } = useInvite()


    useEffect(() => {
        unreadCountsState.checkUserMessages()
    }, [sessionService.currentUserId])

    useEffect(() => {
        // only check gov email when there's no department — it's the only case it matters
        if (currentUserController.selectedDepartmentRole) return

        const email = currentUserController.user?.email
        if (!email) return

        const emailDomain = email.substring(email.lastIndexOf('@') + 1)

        verifiedEmailDomainController.getVerifiedEmailDomain(emailDomain)
            .then(domain => setIsGovernmentEmail(!!domain))
            .catch(() => setIsGovernmentEmail(false))
    }, [currentUserController.selectedDepartmentRole, currentUserController.user, verifiedEmailDomainController])

    const notificationsEnabled = pushService?.notificationPermission === 'granted' && userSettingsController?.notificationsEnabled

    const handleEnableNotifications = () => {
        navigate('/account/notifications')
    }

    const handleAddPosition = () => {
        navigate(`/account/departments/${sessionService.currentUserId}?setPosition=true`)
    }

    const handleJoinAgency = () => {
        setIsJoinDepartmentModalOpen(true)
    }

    const handleStartMessaging = () => {
        const role = currentUserController.selectedDepartmentRole

        if (!role) {
            if (isGovernmentEmail) {
                openJoinRoom('public')
            } else {
                openInvite('contact')
            }
            return
        }

        if (role === 'guest') {
            openCreateThread()
            return
        }

        openJoinRoom('department')
    }

    const actions = [
        {
            icon: 'bell',
            background: 'bg-success',
            label: 'Enable Notifications',
            onClick: handleEnableNotifications
        },
        sessionService.selectedDepartmentId
            ? {
                icon: 'user',
                background: 'bg-pink',
                label: 'Add Your Position',
                onClick: handleAddPosition
            }
            : {
                icon: 'plus',
                background: 'bg-yellow',
                label: 'Join an Agency',
                onClick: handleJoinAgency
            },
        {
            icon: 'message',
            background: 'bg-blue',
            label: 'Start Messaging',
            onClick: handleStartMessaging
        }
    ]

    const getActionIcon = (index: number) => {
        const checkConditions: Record<number, boolean> = {
            0: notificationsEnabled,
            1: !!(sessionService.selectedDepartmentId && currentUserController.selectedDepartmentsAccess?.positionString),
            2: unreadCountsState.hasUserSentMessages
        }

        return checkConditions[index]
            ? <Icon name="check-circle" color="success" type="solid" size={20} />
            : <Icon name="chevron-right" />
    }

    return (
        <div className="animated fadeIn">
            <div className="mt-3 d-flex flex-column flex-lg-row gap-3 w-100 pe-5">
                {actions.map((action, index) => (
                    <Card
                        key={index}
                        as="button"
                        className="d-flex flex-row align-items-center p-3 w-100 onboarding-option"
                        onClick={action.onClick}
                        style={{ maxWidth: 350}}
                    >
                        <div
                            className={`mb-2 mb-md-0 me-md-3 d-flex align-items-center justify-content-center text-${action.background} rounded-3`}
                            style={{ width: '40px', height: '40px' }}
                        >
                            <Icon color="tertiary-bg" name={action.icon as IconName} type="solid" size="lg" faProps={{ title: action.label}}/>
                        </div>
                        <div className="d-flex align-items-center justify-content-between flex-grow-1">
                            <span className="fw-semibold">{action.label}</span>
                            {getActionIcon(index)}
                        </div>
                    </Card>
                ))}
            </div>
            <JoinDepartmentModal
                isOpen={isJoinDepartmentModalOpen}
                onClose={() => setIsJoinDepartmentModalOpen(false)}
            />
        </div>
    )
})

export { OnboardingStepsAction }
